// frontend/src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import useAuth from './hooks/useAuth';
import { AnimatePresence, motion } from 'framer-motion'; // Added Framer Motion imports

import UnifiedNavbar from './components/Layout/UnifiedNavbar';
import LandingNavbar from './components/LandingPage/LandingNavbar'; // Import the new LandingNavbar
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResearchNewsFeedPage from './pages/ResearchNewsFeedPage';
import SavedArticlesPage from './pages/SavedArticlesPage';
import LoadingSpinner from './components/Common/LoadingSpinner';
import EmailVerificationPage from './pages/EmailVerificationPage';
import VerificationPage from './pages/VerificationPage';
import PasswordResetRequestPage from './pages/PasswordResetRequestPage';
import PasswordResetConfirmPage from './pages/PasswordResetConfirmPage';
import AccountPage from './pages/AccountPage';
import TopicInsightsPage from './pages/TopicInsightsPage';
import DailyDigestPage from './pages/DailyDigestPage';
import FullDigestViewPage from './pages/FullDigestViewPage';
import SourcePage from './pages/SourcePage';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 },
};

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.4,
};

const PageWrapper = ({ children }) => (
  <motion.div
    initial="initial"
    animate="in"
    exit="out"
    variants={pageVariants}
    transition={pageTransition}
  >
    {children}
  </motion.div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-b from-white to-blue-50">
        <LoadingSpinner />
      </div>
    );
  }
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-b from-white to-blue-50">
        <LoadingSpinner />
      </div>
    );
  }
  return isAuthenticated ? <Navigate to="/article-feed" replace /> : children;
};

const AppLayout = ({ children }) => {
    const location = useLocation();
    const isLandingPage = location.pathname === '/';
    const authPaths = ['/login', '/register', '/password-reset', '/auth-builtin/reset', '/verify-email'];
    const isAuthPage = authPaths.some(path => location.pathname.startsWith(path));

    // Define paths where the standard app wrapper (padding, footer) should NOT apply as much
    const specialLayoutPaths = [
        { path: '/login', noFooter: true, noPadding: true },
        { path: '/register', noFooter: true, noPadding: true },
        { path: '/verify-email', noFooter: true, noPadding: true },
        { path: '/password-reset', noFooter: true, noPadding: true },
        { path: '/auth-builtin/reset', noFooter: true, noPadding: true },
        { path: '/digest/', noFooter: true, noPadding: false },
        { path: '/article-feed', noFooter: true, noPadding: true }
    ];

    let applyAppShell = true;
    let showFooter = !isLandingPage && !isAuthPage; // Hide footer on landing and auth pages
    let needsTopPadding = !isLandingPage && !isAuthPage; // No top padding for landing and auth pages by default

    for (const p of specialLayoutPaths) {
        if (location.pathname.startsWith(p.path)) {
            if (p.noFooter) showFooter = false;
            if (p.noPadding) needsTopPadding = false;
            if (p.noPadding || p.path === '/article-feed' || p.path === '/digest/') {
                 applyAppShell = false;
            }
            break;
        }
    }

    // Determine which Navbar to show
    const NavbarToRender = isLandingPage || isAuthPage ? LandingNavbar : UnifiedNavbar;

    return (
        <div className={`flex flex-col min-h-screen ${isLandingPage || isAuthPage ? 'bg-white' : 'bg-gray-50'}`}>
            <NavbarToRender />
            <main className={`flex-grow ${needsTopPadding ? 'pt-16 md:pt-20' : ''} ${applyAppShell ? 'px-4 py-8 md:px-8 md:py-10' : ''}`}>
                <AnimatePresence mode="wait">
                    {React.cloneElement(children, { key: location.pathname })}
                </AnimatePresence>
            </main>
            {showFooter && (
                <footer className="bg-gray-800 text-white p-6 text-center text-sm">
                    © {new Date().getFullYear()} Agent Digest. All rights reserved.
                    <div className="mt-2">
                        <Link to="/privacy" className="hover:underline mx-2">Privacy Policy</Link>
                        <Link to="/terms" className="hover:underline mx-2">Terms of Service</Link>
                    </div>
                </footer>
            )}
        </div>
    );
};

const NotFoundPageContent = () => {
    const { isAuthenticated } = useAuth();
    return (
        <div className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50 text-center flex flex-col justify-center items-center flex-grow">
            <div className="container mx-auto px-6">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">404 - Page Not Found</h2>
                <p className="text-gray-600 mb-8">Sorry, the page you are looking for does not exist.</p>
                <Link
                    to={isAuthenticated ? "/article-feed" : "/"}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg"
                >
                    Go Back
                </Link>
            </div>
        </div>
    );
};

const AppRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait"> {/* Changed exitBeforeEnter to mode="wait" for newer Framer Motion versions */}
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageWrapper><LandingPage /></PageWrapper>} />
        <Route path="/login" element={<PublicRoute><PageWrapper><LoginPage /></PageWrapper></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><PageWrapper><RegisterPage /></PageWrapper></PublicRoute>} />
        <Route path="/verify-email" element={<PublicRoute><PageWrapper><EmailVerificationPage /></PageWrapper></PublicRoute>} />
        <Route path="/verification" element={<PageWrapper><VerificationPage /></PageWrapper>} />
        <Route path="/password-reset" element={<PublicRoute><PageWrapper><PasswordResetRequestPage /></PageWrapper></PublicRoute>} />
        <Route path="/auth-builtin/reset/:uid/:token" element={<PublicRoute><PageWrapper><PasswordResetConfirmPage /></PageWrapper></PublicRoute>} />
        
        <Route path="/dashboard" element={<Navigate to="/article-feed" replace />} />
        <Route path="/article-feed" element={<ProtectedRoute><PageWrapper><ResearchNewsFeedPage /></PageWrapper></ProtectedRoute>} />
        <Route path="/saved-articles" element={<ProtectedRoute><PageWrapper><SavedArticlesPage /></PageWrapper></ProtectedRoute>} />
        {/* REMOVED: Sources page route to hide data sources from users */}
        {/* <Route path="/sources" element={<ProtectedRoute><PageWrapper><SourcePage /></PageWrapper></ProtectedRoute>} /> */}
        <Route path="/account" element={<ProtectedRoute><PageWrapper><AccountPage /></PageWrapper></ProtectedRoute>} />
        <Route path="/topic-insights" element={<ProtectedRoute><PageWrapper><TopicInsightsPage /></PageWrapper></ProtectedRoute>} />
        <Route path="/daily-digests" element={<ProtectedRoute><PageWrapper><DailyDigestPage /></PageWrapper></ProtectedRoute>} />
        <Route path="/digest/:date" element={<ProtectedRoute><PageWrapper><FullDigestViewPage /></PageWrapper></ProtectedRoute>} />

        <Route path="*" element={<PageWrapper><NotFoundPage /></PageWrapper>} />
      </Routes>
    </AnimatePresence>
  );
};

const NotFoundPage = () => (
  <div className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50 text-center flex flex-col justify-center items-center flex-grow">
    <div className="container mx-auto px-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-4">404 - Page Not Found</h2>
      <p className="text-gray-600 mb-8">Sorry, the page you are looking for does not exist.</p>
      <Link
        to="/"
        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg"
      >
        Go to Homepage
      </Link>
    </div>
  </div>
);

// Main App component
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppLayout>
          <AppRoutes />
        </AppLayout>
      </AuthProvider>
    </Router>
  );
}

// AppContent component to use useLocation for page transitions
const AppContent = () => {
  const location = useLocation();
  // LandingPage is outside the ProtectedRoute/PublicRoute logic if it has its own auth handling
  // or can be part of PublicRoute if it should redirect authenticated users.
  // For this setup, LandingPage is treated as a general public page.

  return (
    <AppLayout>
      <Routes location={location}> {/* Pass location to Routes for AnimatePresence to work */}
        {/* Public routes */}
        <Route path="/" element={<PageWrapper><LandingPage /></PageWrapper>} />
        <Route path="/login" element={<PublicRoute><PageWrapper><LoginPage /></PageWrapper></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><PageWrapper><RegisterPage /></PageWrapper></PublicRoute>} />
        <Route path="/verify-email" element={<PageWrapper><EmailVerificationPage /></PageWrapper>} />
        <Route path="/verification" element={<PageWrapper><VerificationPage /></PageWrapper>} />
        <Route path="/password-reset" element={<PublicRoute><PageWrapper><PasswordResetRequestPage /></PageWrapper></PublicRoute>} />
        <Route path="/auth-builtin/reset/:uid/:token" element={<PublicRoute><PageWrapper><PasswordResetConfirmPage /></PageWrapper></PublicRoute>} />

        {/* Protected Routes (Dashboard and related features) */}
        <Route path="/dashboard" element={<Navigate to="/article-feed" replace />} />
        <Route path="/article-feed" element={<ProtectedRoute><PageWrapper><ResearchNewsFeedPage /></PageWrapper></ProtectedRoute>} />
        <Route path="/saved-articles" element={<ProtectedRoute><PageWrapper><SavedArticlesPage /></PageWrapper></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><PageWrapper><AccountPage /></PageWrapper></ProtectedRoute>} />
        <Route path="/topic-insights" element={<ProtectedRoute><PageWrapper><TopicInsightsPage /></PageWrapper></ProtectedRoute>} />
        <Route path="/daily-digest" element={<ProtectedRoute><PageWrapper><DailyDigestPage /></PageWrapper></ProtectedRoute>} />
        <Route path="/digest/:digestId" element={<ProtectedRoute><PageWrapper><FullDigestViewPage /></PageWrapper></ProtectedRoute>} />
        
        {/* Fallback for undefined routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
};

export default App;
